// Single source of truth for the product catalog.
//
// Every price here is taken from the printed wholesale flyer. Quantity breaks
// live in `tiers`, and anything that used to be a separate product purely
// because it was priced differently — wax dipping, frame size, feeder size —
// is now a row in `variants`. That is why there is no longer a "10 Frame
// Unassembled Deep Wax Dipped" product: it is the second variant of id 1.
//
// `price`, `waxDippable` and WAX_DIP_SURCHARGE below are retained ONLY so the
// currently-disabled cart path (Cart.tsx / CartContext.tsx / Checkout.tsx)
// keeps compiling untouched. The storefront does not read them.
//
// NOT YET IN SYNC: lambda/order-handler/products.js is still the pre-flyer
// table (ids 1-10 at the old prices). It must be rewritten from this file
// before checkout is re-enabled, or ids 11-15 will be rejected as
// "Unrecognised product in cart" and the other prices will be wrong.

/** Legacy: the flyer prices wax dipping per product, not as a flat surcharge. */
export const WAX_DIP_SURCHARGE = 2;

/** A price cell: a dollar amount, or a tier quoted by phone. */
export type Price = number | 'CALL';

/** One quantity band. Labels are reproduced exactly as the flyer prints them.
 *  Deliberately not stored as numeric min/max yet: the flyer's bands overlap
 *  at their edges (qty 100 and 200 appear in two rows on several products),
 *  and both 10-Frame Deep and 10-Frame Medium jump 100-200 straight to 300+,
 *  leaving 201-299 with no band. Those need resolving with the supplier
 *  before a tier can be resolved in code. */
export interface Tier {
  label: string;
}

/** One row of a price table. `label` is the variant name; it is the empty
 *  string for products whose table has a single unnamed row. */
export interface Variant {
  label: string;
  prices: Price[];
}

/** Display order of the catalog, and the section headings on the home page. */
export const GROUPS = [
  'Hive Boxes & Nucs',
  'Frames',
  'Covers, Boards & Excluders',
  'Feeders',
  'Queen Rearing'
] as const;

export type ProductGroup = (typeof GROUPS)[number];

export interface Product {
  id: number;
  name: string;
  group: ProductGroup;
  images: string[];
  imageAlt: string;
  /** Short supporting line under the name, where the flyer gives one. */
  spec?: string;
  /** A commercial offer worth surfacing — rendered as a pill, not grey caption
   *  text, so it does not read like a part number. */
  perk?: string;
  /** Entry price: quantity 1 of the first variant. Present so the disabled
   *  cart path still type-checks; the storefront renders the table instead. */
  price: number;
  /** Quantity bands. Empty for flat-priced products. */
  tiers: Tier[];
  /** Price rows. Empty for flat-priced products. */
  variants: Variant[];
  /** Unit beside a flat price, e.g. 'each', 'per 1,000-pack'. */
  unit?: string;
  /** Legacy cart flag. Wax dipping is a variant row now, so this is always
   *  false — a dipped item is chosen from the table, not added as a surcharge. */
  waxDippable: boolean;
  isBestSeller?: boolean;
}

const T = (...labels: string[]): Tier[] => labels.map(label => ({ label }));

export const products: Product[] = [
  // ---------------------------------------------------------------- boxes
  {
    id: 6,
    name: '5-Frame Plastic Nuc Box',
    group: 'Hive Boxes & Nucs',
    images: [
      '/images/products/nuc_box_plastic_5_frame_assembled_2.jpg',
      '/images/products/nuc_box_plastic_5_frame_assembled_1.jpg',
      '/images/products/nuc_box_plastic_5_frame_assembled_3.jpg'
    ],
    imageAlt: '5-Frame Plastic Nuc Box, assembled',
    perk: 'Free shipping on 500+',
    price: 9,
    tiers: [],
    variants: [],
    unit: 'each',
    waxDippable: false,
    isBestSeller: true
  },
  {
    id: 1,
    name: '10-Frame Deep',
    group: 'Hive Boxes & Nucs',
    images: [
      '/images/products/10_frame_deep_unassembled.jpg',
      '/images/products/10_frame_unassembled_deep_wax_dipped_1.jpg',
      '/images/products/10_frame_unassembled_deep_wax_dipped_2.jpg',
      '/images/products/10_frame_unassembled_deep_wax_dipped_3.jpg'
    ],
    imageAlt: '10-Frame Deep hive body, unassembled',
    price: 13,
    tiers: T('1 – 99', '100 – 200', '300+'),
    variants: [
      { label: 'Unassembled', prices: [13, 12, 'CALL'] },
      { label: 'Wax dipped', prices: [14, 13, 'CALL'] },
      { label: 'Copper treated', prices: [15, 14, 'CALL'] }
    ],
    waxDippable: false
  },
  {
    id: 11,
    name: '10-Frame Medium',
    group: 'Hive Boxes & Nucs',
    images: ['/images/products/10_frame_medium_unassembled.jpg'],
    imageAlt: '10-Frame Medium hive body, unassembled',
    price: 12,
    tiers: T('1 – 99', '100 – 200', '300+'),
    variants: [
      { label: 'Unassembled', prices: [12, 11, 'CALL'] },
      { label: 'Wax dipped', prices: [13, 12, 'CALL'] }
    ],
    waxDippable: false
  },
  {
    id: 12,
    name: '5-Frame Deep Nuc Box',
    group: 'Hive Boxes & Nucs',
    images: ['/images/products/nuc_box_5_frame_deep_unassembled.jpg'],
    imageAlt: '5-Frame Deep nuc box body, unassembled',
    price: 12,
    tiers: T('1 – 99', '100 – 200', '200+'),
    variants: [
      { label: 'Unassembled', prices: [12, 11, 'CALL'] },
      { label: 'Wax dipped', prices: [13, 12, 'CALL'] }
    ],
    waxDippable: false
  },
  {
    id: 7,
    name: '5-Frame Wooden Nuc Box',
    group: 'Hive Boxes & Nucs',
    images: [
      '/images/products/nuc_box_wooden_5_frame_assembled_1.jpg',
      '/images/products/nuc_box_wooden_5_frame_assembled_2.jpg',
      '/images/products/nuc_box_wooden_5_frame_assembled_3.jpg',
      '/images/products/nuc_box_wooden_5_frame_assembled_4.jpg'
    ],
    imageAlt: '5-Frame Wooden Nuc Box, assembled',
    price: 40,
    tiers: [],
    variants: [],
    unit: 'each',
    waxDippable: false
  },

  // --------------------------------------------------------------- frames
  {
    id: 8,
    name: 'Assembled Frames',
    group: 'Frames',
    images: [
      '/images/products/triple_assembled_wax_foundation_frame.jpg',
      '/images/products/assembled_frame_angled.jpg'
    ],
    imageAlt: 'Assembled frame with wax foundation',
    // The foundation detail lives in `spec`, worded as the flyer has it. As
    // row labels ("Medium · double") it broke across lines in the narrow
    // label column on phones and tablets. Non-breaking spaces keep each half
    // whole, so a wrap falls at the dot rather than after "Medium:".
    spec: 'Deep: triple-wax foundation · Medium: double foundation',
    price: 2.6,
    tiers: T('1 – 199', '200 – 600', '600+'),
    variants: [
      { label: 'Deep', prices: [2.6, 2.5, 'CALL'] },
      { label: 'Medium', prices: [2.6, 2.5, 'CALL'] }
    ],
    waxDippable: false,
    isBestSeller: true
  },

  // ------------------------------------------- covers, boards, excluders
  {
    id: 2,
    name: 'Wax Dipped Bottom Board',
    group: 'Covers, Boards & Excluders',
    images: [
      '/images/products/bottom_board_solid_1.jpg',
      '/images/products/bottom_board_solid_2.jpg'
    ],
    imageAlt: 'Wax dipped bottom board',
    price: 15,
    tiers: T('1 – 49', '50 – 100', '100+'),
    variants: [{ label: 'each', prices: [15, 14, 'CALL'] }],
    waxDippable: false
  },
  {
    id: 5,
    name: 'Inner Cover',
    group: 'Covers, Boards & Excluders',
    images: ['/images/products/inner_cover.jpg'],
    imageAlt: 'Inner cover',
    price: 10,
    tiers: [],
    variants: [],
    unit: 'each',
    waxDippable: false
  },
  {
    id: 4,
    name: 'Galvanized Metal Cover',
    group: 'Covers, Boards & Excluders',
    images: [
      '/images/products/galvanized_metal_cover_2.jpg',
      '/images/products/galvanized_metal_cover_1.jpg',
      '/images/products/galvanized_metal_cover_3.jpg'
    ],
    imageAlt: 'Galvanized metal telescoping cover',
    price: 28,
    tiers: T('1 – 50', '50 – 100', '100+'),
    variants: [{ label: 'each', prices: [28, 25, 'CALL'] }],
    waxDippable: false
  },
  {
    id: 9,
    name: 'Wax Dipped Migratory Lid',
    group: 'Covers, Boards & Excluders',
    images: [
      '/images/products/migratory_lid_1.jpg',
      '/images/products/migratory_lid_2.jpg'
    ],
    imageAlt: 'Wax dipped migratory lid',
    price: 15,
    tiers: T('1 – 50', '50 – 100', '100+'),
    variants: [{ label: 'each', prices: [15, 14, 'CALL'] }],
    waxDippable: false
  },
  {
    id: 13,
    name: 'Metal Queen Excluder',
    group: 'Covers, Boards & Excluders',
    images: ['/images/products/metal_queen_excluder.jpg'],
    imageAlt: 'Metal queen excluder',
    price: 10,
    tiers: T('1 – 50', '50 – 100', '100+'),
    variants: [{ label: 'each', prices: [10, 9, 'CALL'] }],
    waxDippable: false
  },

  // -------------------------------------------------------------- feeders
  {
    id: 3,
    name: 'Feeder with Cap and Ladder',
    group: 'Feeders',
    images: [
      // The labelled shot is the 1.5 gallon; the plain one is the 1 gallon.
      // Both were previously filed under this id as if they were one size.
      '/images/products/cap_and_ladder_feeder_with-1-1:2_gallon_2.jpg',
      '/images/products/cap_and_ladder_feeder_with-1-1:2_gallon.jpg'
    ],
    imageAlt: 'Hive top feeder with cap and ladder',
    spec: 'FD827 cap · FD848 ladder',
    price: 10,
    tiers: T('1 – 50', '51 – 200', '200+'),
    variants: [
      { label: '1.5 gallon', prices: [10, 9, 'CALL'] },
      { label: '1 gallon', prices: [10, 9, 'CALL'] }
    ],
    waxDippable: false
  },

  // -------------------------------------------------------- queen rearing
  {
    id: 14,
    name: 'Base Cell Cups',
    group: 'Queen Rearing',
    images: [
      '/images/products/base_cell_cups.jpg',
      '/images/products/base_cell_cups_2.jpg'
    ],
    imageAlt: 'Plastic queen-rearing base cell cups',
    spec: '16 mm × 19 mm',
    perk: 'Free shipping',
    price: 70,
    tiers: [],
    variants: [],
    unit: 'per 1,000-pack',
    waxDippable: false
  },
  {
    id: 15,
    name: 'Queen Cages',
    group: 'Queen Rearing',
    images: ['/images/products/queen_cages.jpg'],
    imageAlt: 'Plastic queen cages',
    price: 28,
    tiers: [],
    variants: [],
    unit: 'per 100-pack',
    waxDippable: false
  }
];

const byId = new Map(products.map(p => [p.id, p]));

export const getProduct = (id: number): Product | undefined => byId.get(id);

export const isWaxDippable = (id: number): boolean => byId.get(id)?.waxDippable ?? false;

/** "$9" for whole dollars, "$2.50" otherwise — matches the existing display. */
export const formatPrice = (amount: number): string =>
  Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;

/** Always two decimals, so a column of prices keeps its decimal points aligned.
 *  `formatPrice` cannot be used here: it would print "$13" beside "$2.50". */
export const formatTier = (p: Price): string =>
  p === 'CALL' ? 'CALL' : `$${p.toFixed(2)}`;

/** Row thumbnails render at 80-120px, so they must not load the 800px original.
 *  A 240px copy of every product image lives alongside it under `thumbs/`. */
export const thumbSrc = (src: string): string =>
  src.replace('/images/products/', '/images/products/thumbs/');

/** Products in `group`, in catalog order. */
export const productsInGroup = (group: ProductGroup): Product[] =>
  products.filter(p => p.group === group);
